import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Send, Trash2, Bot, User } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

export const Route = createFileRoute('/chat-test')({
  head: () => ({
    meta: [{ title: 'Chat Test · Replya Internal Backoffice' }],
  }),
  component: ChatTestPage,
});

interface Message {
  role: 'user' | 'assistant' | 'error';
  content: string;
}

function TypingIndicator() {
  return (
    <div className="w-fit rounded-2xl bg-surface-2 px-4 py-3 text-sm flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-typing-dot" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-typing-dot" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-typing-dot" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

function ChatTestPage() {
  const { selectedTenant } = useAppState();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: (message: string) => apiService.sendChatMessage(selectedTenant!.tenantId, message),
    onSuccess: (response) => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: String(response.reply ?? 'Nessuna risposta') },
      ]);
    },
    onError: (error) => {
      setMessages((prev) => [...prev, { role: 'error', content: error.message }]);
    },
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, mutation.isPending]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || mutation.isPending) return;
    setMessages((prev) => [...prev, { role: 'user', content: input }]);
    mutation.mutate(input);
    setInput('');
  };

  const clearChat = () => {
    setMessages([]);
    mutation.reset();
  };

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-4 animate-fadeIn">
      <PageHeader title="Chat Test" subtitle={`Testa il chatbot per ${selectedTenant.name}.`}>
        <Button variant="outline" size="sm" className="rounded-xl" onClick={clearChat}>
          <Trash2 className="mr-2 h-4 w-4" />
          Cancella
        </Button>
      </PageHeader>

      <Card className="flex flex-1 flex-col rounded-2xl border bg-surface overflow-hidden">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-4 w-4 text-brand" />
            Conversazione
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4 p-4">
          <ScrollArea className="flex-1 pr-2">
            <div className="space-y-4 min-h-0">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Scrivi un messaggio per iniziare il test.
                </p>
              ) : (
                messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                        msg.role === 'user'
                          ? 'bg-brand text-brand-foreground'
                          : msg.role === 'error'
                          ? 'bg-destructive text-destructive-foreground'
                          : 'bg-surface-3 text-foreground'
                      }`}
                    >
                      {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                    </div>
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                        msg.role === 'user'
                          ? 'bg-brand text-brand-foreground'
                          : msg.role === 'error'
                          ? 'bg-destructive/10 text-destructive border border-destructive/30'
                          : 'bg-surface-2 text-foreground'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
              {mutation.isPending && (
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-surface-3 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-foreground" />
                  </div>
                  <TypingIndicator />
                </div>
              )}
              <div ref={scrollRef} />
            </div>
          </ScrollArea>

          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi un messaggio..."
              disabled={mutation.isPending}
              className="rounded-xl bg-surface-2 border-border"
            />
            <Button type="submit" disabled={mutation.isPending} className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground">
              <Send className="mr-2 h-4 w-4" />
              Invia
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
